// Zod input schemas validated at the server boundary. Client uses the
// inferred types as the source of truth for form payloads.
import { z } from "zod";
import {
  JOB_PRIORITIES,
  SPOOL_STATUSES,
  STAGE_KEYS,
  STAGE_STATUSES,
} from "./enums";

// Status labels are runtime-managed in production_job_statuses (the
// admin can add/rename/archive at any time), so the wire schema
// only validates shape: a non-empty string within the column width.
// Server route handlers cross-check the value against the live
// catalogue before insert/update.
const statusLabel = z.string().trim().min(1).max(32);

const isoDate = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, "Expected YYYY-MM-DD");
const optionalIsoDate = isoDate.nullable().optional();

export const SpoolInput = z.object({
  id: z.number().int().positive().optional(),
  num: z.string().min(1).max(64),
  description: z.string().max(512).nullable().optional(),
  hours: z.number().min(0).max(99999).default(0),
  cutStart: optionalIsoDate,
  cutDone: optionalIsoDate,
  weldStart: optionalIsoDate,
  weldDone: optionalIsoDate,
  dueDate: optionalIsoDate,
  status: z.enum(SPOOL_STATUSES).default("pending"),
  sortOrder: z.number().int().min(0).default(0),
});
export type SpoolInputT = z.infer<typeof SpoolInput>;

export const SpoolBulkReplaceInput = z.object({
  spools: z.array(SpoolInput).max(500),
});
export type SpoolBulkReplaceInputT = z.infer<typeof SpoolBulkReplaceInput>;

// Nominal labour budget per stage — { stageKey: hours }. Keys are
// live stage keys or WELD_NOMINAL_KEY; the route layer drops keys
// that no longer resolve to an hours-carrying catalogue stage rather
// than rejecting the whole save (an archived stage shouldn't block
// an unrelated job edit). Key count is capped so a runaway client
// can't grow the jsonb blob without bound.
export const NominalStageHoursInput = z
  .record(z.string().min(1).max(64), z.number().min(0).max(99999))
  .refine((m) => Object.keys(m).length <= 100, "Too many stage keys");
export type NominalStageHoursInputT = z.infer<typeof NominalStageHoursInput>;

export const JobCreateInput = z.object({
  jobNumber: z.string().max(32).optional(),
  name: z.string().min(1).max(255),
  customer: z.string().max(255).nullable().optional(),
  contact: z.string().max(255).nullable().optional(),
  location: z.string().max(255).nullable().optional(),
  notes: z.string().max(8000).nullable().optional(),
  solutionTypeId: z.number().int().positive().nullable().optional(),
  status: statusLabel.default("New"),
  priority: z.enum(JOB_PRIORITIES).default("Normal"),
  bay: z.number().int().min(1).max(8).nullable().optional(),
  assigneeId: z.number().int().positive().nullable().optional(),
  startDate: optionalIsoDate,
  dueDate: optionalIsoDate,
  opportunityId: z.number().int().positive().nullable().optional(),
  customerId: z.number().int().positive().nullable().optional(),
  sharepointUrl: z.preprocess(
    (v) => (typeof v === "string" && v.trim() === "" ? null : v),
    z.string().url().max(2048).nullable().optional(),
  ),
  // AUD; cents allowed but expected as whole dollars from the UI.
  // Upper bound matches the numeric(14,2) column on production_jobs.
  orderValue: z.number().nonnegative().max(999_999_999_999.99).nullable().optional(),
  // null clears the map back to {} (the column is NOT NULL).
  nominalStageHours: NominalStageHoursInput.nullable().optional(),
  simpleMode: z.boolean().optional(),
  spools: z.array(SpoolInput).max(500).optional(),
});
export type JobCreateInputT = z.infer<typeof JobCreateInput>;

export const JobUpdateInput = JobCreateInput.partial().omit({ spools: true });
export type JobUpdateInputT = z.infer<typeof JobUpdateInput>;

export const TeamMemberInput = z.object({
  name: z.string().min(1).max(120),
  role: z.string().min(1).max(120),
  initials: z.string().min(1).max(4),
  color: z.string().regex(/^#[0-9a-fA-F]{3,8}$/, "Expected hex colour"),
  sortOrder: z.number().int().min(0).default(0),
  active: z.boolean().default(true),
});
export type TeamMemberInputT = z.infer<typeof TeamMemberInput>;

export const TeamMemberUpdateInput = TeamMemberInput.partial();
export type TeamMemberUpdateInputT = z.infer<typeof TeamMemberUpdateInput>;

export const SolutionTypeInput = z.object({
  name: z.string().min(1).max(64),
  sortOrder: z.number().int().min(0).default(0),
  active: z.boolean().default(true),
});
export type SolutionTypeInputT = z.infer<typeof SolutionTypeInput>;

export const SolutionTypeUpdateInput = SolutionTypeInput.partial();
export type SolutionTypeUpdateInputT = z.infer<typeof SolutionTypeUpdateInput>;

// Per-stage default assignee. Server upserts by stage_key — the
// canonical 17 stages are the only valid keys.
export const StageDefaultInput = z.object({
  defaultAssigneeId: z.number().int().positive().nullable(),
});
export type StageDefaultInputT = z.infer<typeof StageDefaultInput>;

// Stage catalogue editing. New custom rows go through StageConfigInput
// (server normalises stage_key to a slug); existing canonical or
// custom rows are mutated via StageConfigUpdateInput.
const colorHex = z.string().regex(/^#[0-9a-fA-F]{3,8}$/, "Expected hex colour");

export const StageConfigInput = z.object({
  stageKey: z.string().min(1).max(64).optional(),
  short: z.string().min(1).max(8),
  name: z.string().min(1).max(64),
  color: colorHex,
  hasHours: z.boolean().default(true),
  weld: z.boolean().default(false),
  bay: z.number().int().min(1).max(8).nullable().optional(),
  sortOrder: z.number().int().min(0).max(9999).optional(),
  weeklyCapacityHours: z.number().min(0).max(1000).optional(),
});
export type StageConfigInputT = z.infer<typeof StageConfigInput>;

export const StageConfigUpdateInput = z.object({
  short: z.string().min(1).max(8).optional(),
  name: z.string().min(1).max(64).optional(),
  color: colorHex.optional(),
  hasHours: z.boolean().optional(),
  weld: z.boolean().optional(),
  bay: z.number().int().min(1).max(8).nullable().optional(),
  sortOrder: z.number().int().min(0).max(9999).optional(),
  weeklyCapacityHours: z.number().min(0).max(1000).optional(),
  archived: z.boolean().optional(),
});
export type StageConfigUpdateInputT = z.infer<typeof StageConfigUpdateInput>;

// Payload passed from a future opportunities tool via sessionStorage
// (key `bsp:prod-schedule:auto-import`). Validated on the client before
// opening the JobModal so a malformed payload never corrupts the form.
export const AutoImportPayload = JobCreateInput.pick({
  name: true,
  customer: true,
  notes: true,
  solutionTypeId: true,
  priority: true,
  dueDate: true,
  opportunityId: true,
  customerId: true,
});
export type AutoImportPayloadT = z.infer<typeof AutoImportPayload>;

// Stage edit payload. PATCH /spools/:id/stages/:stageKey accepts a
// partial — only fields present are touched. Set hours to 0 + status
// to skipped to mark a stage as not needed.
export const SpoolStageUpdateInput = z.object({
  status: z.enum(STAGE_STATUSES).optional(),
  hours: z.number().min(0).max(99999).optional(),
  assigneeId: z.number().int().positive().nullable().optional(),
  startDate: optionalIsoDate,
  doneDate: optionalIsoDate,
});

// Bulk seed/replace for a job's Simple-View stage pipeline. The
// client owns the authoritative draft (deriveJobStages on first
// flip), so the server wipes + re-inserts rather than diffing.
export const JobStageSeedRowInput = z.object({
  stageKey: z.string().min(1).max(64),
  status: z.enum(STAGE_STATUSES),
  hours: z.number().min(0).max(99999),
  assigneeId: z.number().int().positive().nullable(),
  startDate: optionalIsoDate,
  doneDate: optionalIsoDate,
});
export const JobStagesBulkReplaceInput = z.object({
  stages: z.array(JobStageSeedRowInput).max(100),
});
export type JobStagesBulkReplaceInputT = z.infer<typeof JobStagesBulkReplaceInput>;

// Distribute the job's nominal labour budget evenly across its
// spools, writing production_spool_stages.hours. `nominalStageHours`
// overrides the persisted map so the Spools tab can distribute what
// the user just typed on the Details tab without a Save round-trip
// first; omit it to use whatever the job row carries.
export const NominalDistributeInput = z.object({
  nominalStageHours: NominalStageHoursInput.optional(),
});
export type NominalDistributeInputT = z.infer<typeof NominalDistributeInput>;
export type SpoolStageUpdateInputT = z.infer<typeof SpoolStageUpdateInput>;

export const StageKeySchema = z.enum(STAGE_KEYS as [string, ...string[]]);

// Job conversation post. authorName comes from the auth session; only
// the body is user-provided. Limit lifted to 32 KB to accommodate
// the Tiptap WYSIWYG output (tags + attributes inflate the byte
// count even for short visible messages).
export const JobMessageInput = z.object({
  body: z.string().min(1).max(32_768),
  // Planner-user UUIDs of every @mention picked in the editor. Empty
  // array (or omitted) means no tag → no push. Capped at 50 to keep
  // a runaway client from blasting the whole tenant; the picker
  // shows 8 at a time so realistic mention counts stay tiny.
  mentions: z.array(z.string().uuid()).max(50).optional(),
});
export type JobMessageInputT = z.infer<typeof JobMessageInput>;

// Drawing registration after a direct-SAS upload finishes. The
// server still has to verify the blob exists at blobPath (Azure
// returns 404 if not), so an injected blobPath is a no-op.
export const DrawingRegisterInput = z.object({
  filename: z.string().min(1).max(255),
  blobPath: z.string().min(1).max(512),
  sizeBytes: z.number().int().min(0).optional(),
});
export type DrawingRegisterInputT = z.infer<typeof DrawingRegisterInput>;

export const JobListFilter = z.object({
  status: statusLabel.optional(),
  // When true, returns jobs whose status is not flagged
  // is_terminal in production_job_statuses (i.e. excludes Complete,
  // Cancelled and any other terminal status the admin defines).
  // Mutually exclusive with `status`: the dropdown either picks a
  // specific label or 'All Active'.
  activeOnly: z.coerce.boolean().optional(),
  priority: z.enum(JOB_PRIORITIES).optional(),
  solutionTypeId: z.coerce.number().int().positive().optional(),
  assigneeId: z.coerce.number().int().positive().optional(),
  bay: z.coerce.number().int().min(1).max(8).optional(),
  q: z.string().max(120).optional(),
  from: isoDate.optional(),
  to: isoDate.optional(),
});
export type JobListFilterT = z.infer<typeof JobListFilter>;

// Catalogue mutation inputs for production_job_statuses. statusKey
// is a stable identifier — POST generates one server-side from the
// label slug; PATCH never lets the client change it. The label is
// what the user types and renames; the server cascades a rename to
// production_jobs.status in the same transaction so existing jobs
// keep their colour swatch and Kanban column.
const statusColorHex = z.string().regex(/^#[0-9A-Fa-f]{6}$/, "Expected #RRGGBB hex");

export const JobStatusCreateInput = z.object({
  label: statusLabel,
  color: statusColorHex.default("#707372"),
  sortOrder: z.number().int().min(0).max(10_000).optional(),
  isTerminal: z.boolean().optional(),
});
export type JobStatusCreateInputT = z.infer<typeof JobStatusCreateInput>;

export const JobStatusUpdateInput = z.object({
  label: statusLabel.optional(),
  color: statusColorHex.optional(),
  sortOrder: z.number().int().min(0).max(10_000).optional(),
  archived: z.boolean().optional(),
  isTerminal: z.boolean().optional(),
});
export type JobStatusUpdateInputT = z.infer<typeof JobStatusUpdateInput>;

// ── Cycle-times catalogue ────────────────────────────────────────
// Pipe sizes: admin manages the catalogue. nbMm gives a numeric
// sortable key alongside the DN label so the grid orders 15 < 100 <
// 150 instead of "DN100" < "DN15" < "DN150".
const pipeLabel = z.string().trim().min(1).max(16);
const minutes = z.number().min(0).max(10_000);

export const PipeSizeCreateInput = z.object({
  label: pipeLabel,
  nbMm: z.number().int().min(1).max(10_000),
  sortOrder: z.number().int().min(0).max(10_000).optional(),
});
export type PipeSizeCreateInputT = z.infer<typeof PipeSizeCreateInput>;

export const PipeSizeUpdateInput = z.object({
  label: pipeLabel.optional(),
  nbMm: z.number().int().min(1).max(10_000).optional(),
  sortOrder: z.number().int().min(0).max(10_000).optional(),
  archived: z.boolean().optional(),
});
export type PipeSizeUpdateInputT = z.infer<typeof PipeSizeUpdateInput>;

export const WeldTypeCreateInput = z.object({
  label: z.string().trim().min(1).max(64),
  sortOrder: z.number().int().min(0).max(10_000).optional(),
});
export type WeldTypeCreateInputT = z.infer<typeof WeldTypeCreateInput>;

export const WeldTypeUpdateInput = z.object({
  label: z.string().trim().min(1).max(64).optional(),
  sortOrder: z.number().int().min(0).max(10_000).optional(),
  archived: z.boolean().optional(),
});
export type WeldTypeUpdateInputT = z.infer<typeof WeldTypeUpdateInput>;

// Single-cell upsert. pipeSizeId NULL = stage-average row.
export const CycleStageRateUpsertInput = z.object({
  stageKey: z.string().trim().min(1).max(64),
  pipeSizeId: z.number().int().positive().nullable(),
  minutes,
});
export type CycleStageRateUpsertInputT = z.infer<typeof CycleStageRateUpsertInput>;

export const CycleWeldRateUpsertInput = z.object({
  weldTypeId: z.number().int().positive(),
  pipeSizeId: z.number().int().positive(),
  minutes,
});
export type CycleWeldRateUpsertInputT = z.infer<typeof CycleWeldRateUpsertInput>;

// Spool composition upsert. weldCounts is the full intended state
// (the route replaces the prior set in a transaction) so the editor
// can submit a single round-trip without diff math on the client.
export const SpoolCompositionUpsertInput = z.object({
  pipeSizeId: z.number().int().positive().nullable(),
  stageCounts: z
    .array(
      z.object({
        stageKey: z.string().trim().min(1).max(64),
        pipeSizeId: z.number().int().positive(),
        count: z.number().int().min(0).max(10_000),
      }),
    )
    .max(500),
  weldCounts: z
    .array(
      z.object({
        weldTypeId: z.number().int().positive(),
        pipeSizeId: z.number().int().positive(),
        count: z.number().int().min(0).max(10_000),
      }),
    )
    .max(500),
});
export type SpoolCompositionUpsertInputT = z.infer<typeof SpoolCompositionUpsertInput>;

// Work calendar inputs (Phase 4).
export const WorkScheduleUpdateInput = z.object({
  mon: z.boolean().optional(),
  tue: z.boolean().optional(),
  wed: z.boolean().optional(),
  thu: z.boolean().optional(),
  fri: z.boolean().optional(),
  sat: z.boolean().optional(),
  sun: z.boolean().optional(),
  hoursPerDay: z.number().min(0.5).max(24).optional(),
});
export type WorkScheduleUpdateInputT = z.infer<typeof WorkScheduleUpdateInput>;

const isoDateOnly = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Expected YYYY-MM-DD");

export const WorkHolidayCreateInput = z.object({
  date: isoDateOnly,
  label: z.string().trim().min(1).max(64),
  recurring: z.boolean().default(false),
});
export type WorkHolidayCreateInputT = z.infer<typeof WorkHolidayCreateInput>;

export const WorkHolidayUpdateInput = z.object({
  date: isoDateOnly.optional(),
  label: z.string().trim().min(1).max(64).optional(),
  recurring: z.boolean().optional(),
});
export type WorkHolidayUpdateInputT = z.infer<typeof WorkHolidayUpdateInput>;

export const StageReorderInput = z.object({
  keys: z.array(z.string().min(1).max(64)).min(1).max(100),
});
export type StageReorderInputT = z.infer<typeof StageReorderInput>;

// Numeric-ID reorder payload shared between pipe sizes + weld types.
export const IdsReorderInput = z.object({
  ids: z.array(z.number().int().positive()).min(1).max(500),
});
export type IdsReorderInputT = z.infer<typeof IdsReorderInput>;

export const JobStatusReorderInput = z.object({
  keys: z.array(z.string().min(1).max(64)).min(1).max(100),
});
export type JobStatusReorderInputT = z.infer<typeof JobStatusReorderInput>;
